import { ChangeDetectorRef } from '@angular/core';
import { FormGroup } from '@angular/forms';

import { BusinessExceptionModel } from '../../../models/core/business-exception.model';

/**
 * Utils.
 */
export class Utils {
    /**
     * Checks whether an item exists or not.
     * @param value Item to be checked.
     */
    static isDefined(value: any): boolean {
        // eslint-disable-next-line no-null/no-null
        return value !== undefined && value !== null;
    }

    /**
     * Checks whether an item is of type object.
     * @param value Item to be checked.
     */
    static isObject(value: any): boolean {
        return typeof value === 'object' && Utils.isDefined(value);
    }

    /**
     * Checks whether an item is of type Promise.
     * @param value Item to be checked.
     */
    static isPromise(value: any): boolean {
        return value instanceof Promise;
    }

    /**
     * Checks whether an item is of type function.
     * @param value Item to be checked.
     */
    static isFunction(value: any): boolean {
        return value instanceof Function;
    }

    /**
     * Checks whether two items or values are equal.
     *
     * Two items or values are considered equal, if at least one of these is true:
     *
     * * Return true for the `===` comparison.
     * * Are of the same type and all their properties, if any, are equal as well compared with `areEqual`.
     *
     * @param obj1 Object or value to be compared.
     * @param obj2 Object or value to be compared.
     */
    static areEqual(obj1: any, obj2: any): boolean {
        if (obj1 === obj2) {
            return true;
        }

        // eslint-disable-next-line no-null/no-null
        if (obj1 === null || obj2 === null) {
            return false;
        }

        if (obj1 !== obj1 && obj2 !== obj2) {
            return true; // NaN === NaN
        }

        const t1 = typeof obj1;
        const t2 = typeof obj2;
        let length: number;
        let key: any;
        let keySet: any;

        if (t1 === t2 && t1 === 'object') {
            if (Array.isArray(obj1)) {
                if (!Array.isArray(obj2)) {
                    return false;
                }

                length = obj1.length;

                if (length === obj2.length) {
                    for (key = 0; key < length; key++) {
                        if (!Utils.areEqual(obj1[key], obj2[key])) {
                            return false;
                        }
                    }

                    return true;
                }
            } else {
                if (Array.isArray(obj2)) {
                    return false;
                }

                keySet = {};

                for (const obj1Key of Object.keys(obj1)) {
                    if (!Utils.areEqual(obj1[obj1Key], obj2[obj1Key])) {
                        return false;
                    }

                    keySet[obj1Key] = true;
                }

                for (key in obj2) {
                    if (!(key in keySet) && typeof obj2[key] !== 'undefined') {
                        return false;
                    }
                }

                return true;
            }
        }

        return false;
    }

    /**
     * Generates a new GUID.
     */
    static newGuid(): string {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, ch => {
            const random = (Math.random() * 16) | 0;
            const val = ch === 'x' ? random : (random & 0x3) | 0x8;

            return val.toString(16);
        });
    }

    /**
     * Saves a file locally.
     * @param blob File as blob.
     * @param name File name.
     * @param openInPopup Should open popup?
     */
    static saveFile(blob: Blob, name: string, openInPopup = false): void {
        const url = window.URL.createObjectURL(blob);

        if (openInPopup) {
            window.open(url, 'popup');
            return;
        }

        const linkElement = document.createElement('a');

        document.body.appendChild(linkElement);
        linkElement.setAttribute('style', 'display: none');

        linkElement.href = url;
        linkElement.download = name;

        linkElement.click();
        window.URL.revokeObjectURL(url);
        linkElement.remove();
    }

    static startFocusTrap(element: HTMLElement, changeDetectorRef: ChangeDetectorRef): void {
        element.focus();
        element.addEventListener('blur', this.onBlur);
        changeDetectorRef.detectChanges();
    }

    static endFocusTrap(element: HTMLElement, changeDetectorRef: ChangeDetectorRef): void {
        element.removeEventListener('blur', this.onBlur);
        element.blur();
        changeDetectorRef.detectChanges();
    }

    static displayBusinessExceptions(formGroup: FormGroup, brokenRules: BusinessExceptionModel[], shouldMarkAsTouched = true): void {
        brokenRules.forEach(ex => {
            const key = Object.keys(formGroup.controls).find(k => k.toLocaleLowerCase() === ex.property.toLocaleLowerCase());

            if (!!key) {
                formGroup.controls[key].setErrors({ error: ex.error });

                if (shouldMarkAsTouched) {
                    formGroup.controls[key].markAsTouched();
                }
            }
        });
    }

    private static onBlur(this: HTMLElement): void {
        this.focus();
    }
}
